import { useCallback, useEffect, useRef, useState } from 'react';
import { PurchasedTickets, ticketService } from '../services/ticketService';
import { readPurchaseSecret } from '../utils/purchaseSecret';

export type TicketsViewState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    // Paid, tickets being issued: polling
    | { kind: 'generating' }
    // Still being issued after the polling window: they will arrive by email
    | { kind: 'delayed' }
    | { kind: 'shown'; tickets: PurchasedTickets }
    | { kind: 'viewed'; maskedEmail?: string }
    // No secret in this browser, or not the browser that bought
    | { kind: 'unavailable' }
    | { kind: 'error' };

export type ResendState = 'idle' | 'sending' | 'sent' | 'limit' | 'error';

// Issuance usually takes a couple of seconds after the payment is confirmed
const POLL_MS = 3_000;
const MAX_POLL_MS = 90_000;

/**
 * The single view of the tickets of a purchase. The tickets live only in this state: once shown, closing and
 * reopening the modal shows them again, but reloading the page loses them (the server answers "already viewed").
 */
export const useTicketsSingleView = (reference: string | null) => {
    const [view, setView] = useState<TicketsViewState>({ kind: 'idle' });
    const [resendState, setResendState] = useState<ResendState>('idle');
    const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pollStartedAt = useRef(0);

    const stopPolling = () => {
        if (pollTimer.current) clearTimeout(pollTimer.current);
        pollTimer.current = null;
    };

    useEffect(() => stopPolling, []);

    const request = useCallback(async () => {
        if (!reference) return;
        const secret = readPurchaseSecret(reference);
        if (!secret) {
            setView({ kind: 'unavailable' });
            return;
        }
        try {
            const result = await ticketService.viewOnce(reference, secret);
            switch (result.status) {
                case 'SHOWN':
                    setView({ kind: 'shown', tickets: result.tickets });
                    break;
                case 'PENDING':
                    if (Date.now() - pollStartedAt.current < MAX_POLL_MS) {
                        setView({ kind: 'generating' });
                        pollTimer.current = setTimeout(request, POLL_MS);
                    } else {
                        setView({ kind: 'delayed' });
                    }
                    break;
                case 'DENIED':
                    setView({ kind: 'unavailable' });
                    break;
                case 'ALREADY_VIEWED':
                    setView({ kind: 'viewed', maskedEmail: result.maskedEmail });
                    break;
            }
        } catch {
            setView({ kind: 'error' });
        }
    }, [reference]);

    const load = useCallback(() => {
        stopPolling();
        pollStartedAt.current = Date.now();
        setView({ kind: 'loading' });
        request();
    }, [request]);

    // Opening again keeps what was already obtained: the view is consumed on the server
    const open = useCallback(() => {
        if (view.kind === 'idle') load();
    }, [view.kind, load]);

    const resend = useCallback(async () => {
        if (!reference) return;
        setResendState('sending');
        try {
            setResendState(await ticketService.resend(reference) === 'ACCEPTED' ? 'sent' : 'limit');
        } catch {
            setResendState('error');
        }
    }, [reference]);

    return { view, resendState, open, retry: load, resend };
};

export type TicketsSingleView = ReturnType<typeof useTicketsSingleView>;
